/*
 * Developed by CQ Inversiones SAS.  Copyright (c) 2025. All rights reserved.
 * Desarrollado por CQ Inversiones SAS.  Copyright (c) 2025. Todos los derechos reservados.
 */

/*
 * Project:      mkad-frontend
 * Developed by: Juan David Pelaez Cumbe
 * Date:         11/03/24 - 8:35
 * Module name:  valid-roles-by-route
 * File name:    valid-roles-by-route.guard.ts
 * IDE:          WebStorm
 */

import {CanActivateFn, Router} from '@angular/router';
import {inject, Signal} from '@angular/core';
import {UserLevelEnum} from '@zibanu/auth/lib/enums/user-level.enum';
import {MkadUserCustomersService} from '@shared/services';
import {ValidationParamsAdministration} from '@core/constants/users-module-permissions.const';
import {ManageSessionStore, ManageSessionStoreType} from '@auth/sotre/management-session.store';
import {UserCustomerDataInterface, UserProfileInterface} from '@shared/interface';
import {catchError, of, zip} from 'rxjs';
import {map} from 'rxjs/operators';
import {toSignal} from '@angular/core/rxjs-interop';
import {ZbAuthToken} from '@zibanu/auth';
import {PartialUserInterface} from '@shared/interface/common/partial-user.interface';

export const ValidRolesByRouteGuard: CanActivateFn = (route) => {
  const dataRole: ValidationParamsAdministration = <ValidationParamsAdministration>route['data'];
  const userCustomer: MkadUserCustomersService = inject(MkadUserCustomersService);
  const management: ManageSessionStoreType = inject(ManageSessionStore);
  const userData = <Signal<ZbAuthToken>>toSignal(management.authServiceRef().getToken());

  const router = inject(Router);
  if ((dataRole.userRole || UserLevelEnum.Guest) === UserLevelEnum.Guest) {
    return router.createUrlTree(['/home']);
  }
  const customerData = management.preferencesRef().userCustomer();

  const profileResponse = (userData: UserProfileInterface) => {
    const {email, first_name, last_name, profile} = userData;
    management.preferencesRef().userSettings = {
      ...profile,
      email,
      first_name,
      last_name,
    };
  };
  const customerResponse = (customerData: UserCustomerDataInterface) => {
    management.preferencesRef().userCustomer.set(customerData);
  };

  const zipGetUserAndCustomerData = () => zip(
    management.preferencesRef().getProfileData(),
    userCustomer.getCustomer()
  );

  if (customerData === null) {
    return zipGetUserAndCustomerData().pipe(
      map(([profile, customer]) => {
        if (profile) profileResponse(profile);

        customerResponse(customer);

        const {user} = <{ user: PartialUserInterface }>userData().getPayload();

        const customerLevel = management.isManagementActive()
          ? management.data().customer_level
          : customer.customer_level;
        if (customerLevel !== undefined) {
          const userLevel: UserLevelEnum = user.level as UserLevelEnum;
          const canAccess =  userLevel <= dataRole.userRole && dataRole.customerRole.includes(customerLevel);
          if (!canAccess) {
            router.navigate(['/home']).then();
            return false;
          }
          return true;
        }
        return router.createUrlTree(['/home']);
      }),
      catchError(() => {
        return of(false);
      }),
    );
  }
  const customerLevel = management.isManagementActive()
    ? management.data().customer_level
    : customerData.customer_level;
  if (customerLevel !== undefined) {
    const userLevel = management.preferencesRef().level;
    return userLevel <= dataRole.userRole && dataRole.customerRole.includes(customerLevel);
  }
  return router.createUrlTree(['/home']);
};

